// frontend/src/pages/admin/ManagePlayers.js
import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import { AuthContext } from "../../context/AuthContext";
import Navbar from "../../components/Navbar";

// Common reusable components
import { validateAadhaar } from "../../components/common/AadhaarValidation";
import Modal from "../../components/common/Modal";
import SearchBar from "../../components/common/SearchBar";
import DataTable from "../../components/common/DataTable";
import ExportButtons from "../../components/common/ExportButtons";
import { successAlert, errorAlert, confirmAlert, warningAlert } from "../../utils/alert";
import Swal from "sweetalert2";
import SearchableSelect from "../../components/common/SearchableSelect";
import DisabilitySelect from "../../components/common/DisabilitySelect";
import MediaViewerModal from "../../components/common/MediaViewerModal";

const norm = (v) => String(v || "").trim().toLowerCase();
const toBool = (v) => {
  if (v === true || v === 1) return true;
  const raw = String(v ?? "").trim().toLowerCase();
  return raw === "1" || raw === "true" || raw === "yes";
};

const normalizeMobile = (value) => {
  if (!value) return "";
  let digits = value.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("0")) {
    digits = digits.substring(1);
  } else if (digits.length === 12 && digits.startsWith("91")) {
    digits = digits.substring(2);
  }
  return digits.substring(0, 10);
};


const extractApiError = (err) => {
  const payload = err?.response?.data || err || {};
  const errors = payload?.errors || null;
  const status = payload?.status || err?.response?.status || null;
  const message = errors
    ? Object.values(errors).flat().join(", ")
    : payload?.message ||
      payload?.error ||
      err?.message ||
      (status ? `Request failed (${status})` : "Server error");

  return { payload, errors, status, message };
};
const batchRequests = async (items, fn, limit = 5) => {
  const results = [];
  for (let i = 0; i < items.length; i += limit) {
    const chunk = items.slice(i, i + limit);
    results.push(...(await Promise.all(chunk.map(fn))));
  }
  return results;
};

const AP_DISTRICTS = [
  "ALLURI SITHARAMA RAJU",
  "ANAKAPALLI",
  "ANANTHAPUR",
  "ANNAMYYA",
  "BAPATLA",
  "CHITTOOR",
  "EAST GODAVARI",
  "ELURU",
  "GUNTUR",
  "KAKINADA",
  "NTR",
  "PALNADU",
  "PARVATHIPURAM MANYAM",
  "PRAKASAM",
  "NANDYAL",
  "SRI POTTI SRIRAMULU NELLORE",
  "SRI SATHYA SAI",
  "SRIKAKULAM",
  "TIRUPATI",
  "VISAKHAPATNAM",
  "VIZIANAGARAM",
  "WEST GODAVARI",
  "YSR KADAPA",
  "KURNOOL",
  "KRISHNA",
  "DR. B.R. AMBEDKAR KONASEEMA",
];

const ManagePlayers = () => {
  const { role } = useContext(AuthContext);
  const roleLower = (role || "").toLowerCase();
  const isAdmin = roleLower === "admin";

  const canManageAccounts = ["admin", "secretary"].includes(
      (role || "").toLowerCase()
  );
  const canManageFeeExemption = ["admin", "secretary", "district"].includes(roleLower);
  const feeExemptionField = roleLower === "district" ? "is_district_fee_exempt" : "is_state_fee_exempt";
  const feeExemptionLabel = roleLower === "district" ? "District" : "State";

  const emptyForm = {
    name: "",
    relation: "",
    dob: "",
    gender: "",
    religion: "",
    caste: "",
    subcaste: "",
    disability_types: [],
    aadhaar_number: "",
    mobile: "",
    email: "",
    district: "",
    mandal: "",
    postoffice: "",
    address: "",
    state: "",
    pincode: "",
    club: "",
    coach: "",
  };

  const [emailError, setEmailError] = useState("");
  const [aadhaarError, setAadhaarError] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [players, setPlayers] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [fileData, setFileData] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewPlayer, setViewPlayer] = useState(null);
  const [mediaModalSrc, setMediaModalSrc] = useState(null);
  const [mediaModalTitle, setMediaModalTitle] = useState("");
  const openMediaModal = (src, title = "Document Preview") => {
    setMediaModalSrc(src);
    setMediaModalTitle(title);
  };
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [existingFiles, setExistingFiles] = useState({});
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [selectedPostOffice, setSelectedPostOffice] = useState("");
  const mandalOptions = useMemo(
    () =>
      [...new Set(pincodeOptions.map((opt) => (opt.mandal || "").trim()))].filter(Boolean),
    [pincodeOptions]
  );
  const combinedDistrictOptions = useMemo(() => {
    const list = new Set(AP_DISTRICTS);
    pincodeOptions.forEach((opt) => {
      if (opt.district && opt.district.trim()) {
        list.add(opt.district.trim().toUpperCase());
      }
    });
    if (formData.district && formData.district.trim()) {
      list.add(formData.district.trim().toUpperCase());
    }
    return Array.from(list).sort();
  }, [pincodeOptions, formData.district]);
  const villageRef = useRef(null);

  // Pagination & Search
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [visibilityFilter, setVisibilityFilter] = useState("visible");
  const [playersRefreshKey, setPlayersRefreshKey] = useState(0);

  const handleSetLimit = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(1);
  };

  // === FETCH PLAYERS ===
  useEffect(() => {
    const controller = new AbortController();

    const fetchPlayers = async () => {
      try {
        const res = await API.get("/admin/players", {
          params: { page, limit, search: searchTerm, visibility: visibilityFilter },
          signal: controller.signal,
        });

        const lastPage = Math.max(Number(res.last_page) || 1, 1);

        // A deletion or other data change can leave the current page beyond
        // the final page. Refetch the nearest valid page instead of showing an
        // empty table beside a non-zero result count.
        if (page > lastPage) {
          setPage(lastPage);
          return;
        }

        setPlayers(res.data || []);
        setTotalPages(lastPage);
        setTotalPlayers(res.total || 0);
        setSelectedIds([]);
      } catch (err) {
        if (
          API.isCancel(err) ||
          err?.isNetworkDrop ||
          err?.name === "CanceledError" ||
          err?.code === "ERR_CANCELED" ||
          err?.code === "ERR_NETWORK" ||
          err?.code === "NO_RESPONSE"
        ) {
          return; // Ignore transient cancellation or network drops quietly
        }
        console.error("Error loading players:", err);
      }
    };

    fetchPlayers();

    return () => {
      controller.abort();
    };
  }, [page, limit, searchTerm, visibilityFilter, playersRefreshKey]);

  const handleVisibilityChange = async (player) => {
    if (!isAdmin) return;
    const willHide = !toBool(player.is_hidden);
    const result = await Swal.fire({
      title: willHide ? "Hide this player?" : "Unhide this player?",
      text: willHide
        ? "The player and all corresponding event registrations will disappear from admin, secretary and district tables until you unhide them."
        : "The player and corresponding event registrations will become visible again.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: willHide ? "Yes, hide" : "Yes, unhide",
    });
    if (!result.isConfirmed) return;
    try {
      const response = await API.put(`/admin/players/${player.id}/visibility`, { is_hidden: willHide });
      successAlert(willHide ? "Player hidden" : "Player unhidden", response?.message || "Visibility updated successfully.");
      setPlayersRefreshKey((key) => key + 1);
    } catch (error) {
      errorAlert("Visibility update failed", error?.message || "Unable to update player visibility.");
    }
  };

  // === HANDLERS ===
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clear server-side errors when user edits
    if (name === "aadhaar_number") {
      setAadhaarError("");
    }
    if (name === "email") {
      setEmailError("");
    }

    if (name === "aadhaar_number") {
      const cleaned = value.replace(/\D/g, "").slice(0, 12);
      setFormData({ ...formData, aadhaar_number: cleaned });

      if (cleaned.length === 12 && !validateAadhaar(cleaned)) {
        setAadhaarError("Invalid Aadhaar number (checksum failed)");
      }
    } 
    else if (name === "email") {
      setFormData({ ...formData, email: value });

      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setEmailError("Invalid email format");
      }
    }
    else if (name === "mobile") {
        setMobileError("");

        setFormData({
            ...formData,
            mobile: normalizeMobile(value)
        });
    }
    else {
      if (name === "pincode") {
        const clean = value.replace(/\D/g, "").slice(0, 6);
        setFormData({
          ...formData,
          pincode: clean,
          mandal: "",
          postoffice: "",
        });
        setSelectedPostOffice("");
        setPincodeOptions([]);
      } else {
        let val = value;
        if (!["dob", "gender"].includes(name)) {
          val = String(value || "").toUpperCase();
        }
        setFormData({ ...formData, [name]: val });
      }
    }
  };

  const handleMobileBlur = async () => {
      const mobile = String(formData.mobile || "").trim();

      if (!mobile) {
          setMobileError("");
          return;
      }

      if (!/^\d{10}$/.test(mobile)) {
          setMobileError("❌ Mobile number must be a valid 10-digit number");
          return;
      }

      try {
          const res = await API.get("/auth/check-mobile", {
              params: {
                  mobile,
                  ignore: editing ? editId : null,
              },
          });

          if (res.exists) {
              setMobileError("❌ Mobile number already exists");
          } else {
              setMobileError("");
          }
      } catch (e) {
          console.error("Mobile check failed:", e);
          setMobileError("❌ Unable to validate Mobile Number");
      }
  };

  useEffect(() => {
    const pincode = String(formData.pincode || "").trim();
    if (pincode.length !== 6) {
      setPincodeOptions([]);
      setSelectedPostOffice("");
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const rows = await API.lookupPincode(pincode);
        const list = Array.isArray(rows) ? rows : [];
        setPincodeOptions(list);
        if (list.length > 0) {
          setTimeout(() => villageRef.current?.focus(), 0);
        }

        const pickBest = () => {
          if (list.length === 0) return null;
          if (list.length === 1) return list[0];
          return (
            list.find((x) => norm(x.postoffice) === norm(formData.postoffice)) ||
            list.find(
              (x) =>
                norm(x.mandal) === norm(formData.mandal) &&
                norm(x.district) === norm(formData.district)
            ) ||
            list.find((x) => norm(x.district) === norm(formData.district)) ||
            list.find((x) => norm(x.mandal) === norm(formData.mandal)) ||
            list[0]
          );
        };

        const best = pickBest();
        setSelectedPostOffice((prev) => {
          if (prev && list.some((x) => norm(x.postoffice) === norm(prev))) {
            return prev;
          }
          return best?.postoffice || "";
        });
        if (best) {
          setFormData((prev) => ({
            ...prev,
            postoffice: prev.postoffice || best.postoffice || "",
            mandal: prev.mandal || best.mandal || "",
            state: prev.state || best.state || "",
          }));
        }
      } catch (err) {
        setPincodeOptions([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [formData.pincode, formData.postoffice, formData.mandal, formData.district]);

  const handleFileChange = (e) =>
    setFileData({ ...fileData, [e.target.name]: e.target.files[0] });

  const handleAadhaarBlur = async () => {
    const aadhaar = formData.aadhaar_number?.trim();

    if (!aadhaar || aadhaar.length !== 12) {
      setAadhaarError("");
      return;
    }

    if (!validateAadhaar(aadhaar)) {
      setAadhaarError("Invalid Aadhaar number (checksum failed)");
      return;
    }

    try {
      const res = await API.get("/auth/check-aadhaar", {
        params: {
          aadhaar,
          ignore: editing ? editId : null,
        },
      });

      // res is already data
      if (res.invalid) {
        setAadhaarError("Invalid Aadhaar number");
      } else if (res.exists) {
        setAadhaarError("Aadhaar number already exists");
      } else {
        setAadhaarError("");
      }
    } catch (e) {
      console.error("Aadhaar check failed:", e);
      setAadhaarError("Unable to validate Aadhaar");
    }
  };

  const handleEmailBlur = async () => {
    const email = formData.email?.trim();

    if (!email) {
      setEmailError("");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Invalid email format");
      return;
    }

    try {
      const res = await API.get("/auth/check-email", {
        params: {
          email,
          ignore: editing ? editId : null,
        },
      });

      // res is already data
      if (res.exists) {
        setEmailError("Email already exists");
      } else {
        setEmailError("");
      }
    } catch (e) {
      console.error("Email check failed:", e);
      setEmailError("Unable to validate email");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous server errors
    setEmailError("");
    setAadhaarError("");

    if (aadhaarError) {
      errorAlert("Aadhaar Validation Error", "Please Check Aadhaar Number");
      return;
    }

    if (emailError) {
      errorAlert("Email Validation Error", "Please Check Email");
      return;
    }

    if (mobileError) {
        errorAlert("Mobile Validation Error", "Please Check Mobile Number");
        return;
    }

    if (!validateAadhaar(formData.aadhaar_number)) {
      errorAlert("Invalid Aadhaar", "Checksum validation failed");
      return;
    }
    if (!Array.isArray(formData.disability_types) || formData.disability_types.length === 0) {
      errorAlert("Disability Type Required", "Please select at least one disability type.");
      return;
    }

    const form = new FormData();
    Object.entries(formData).forEach(([k, v]) => {
      if (k === "disability_types") {
        form.append("disability_types", JSON.stringify(v));
      } else if (v !== null && v !== undefined) {
        form.append(k, v);
      }
    });
    Object.entries(fileData).forEach(([k, f]) => f && form.append(k, f));

    try {
      if (editing) {
        const confirmed = await confirmAlert({
          title: "Update Player?",
          text: "Do you want to save changes?",
          confirmText: "Update",
        });

        if (!confirmed?.isConfirmed) return;

        form.append("_method", "PUT");
        await API.post(`/admin/players/${editId}`, form);
        successAlert("Updated!", "Player updated successfully");
      } else {
        const confirmed = await confirmAlert({
          title: "Add Player?",
          text: "Do you want to add this player?",
          confirmText: "Add",
        });

        if (!confirmed?.isConfirmed) return;

        await API.post("/admin/players", form);
        successAlert("Added!", "Player added successfully");
      }

      setFormData(emptyForm);
      setFileData({});
      setPincodeOptions([]);
      setSelectedPostOffice("");
      setEditing(false);
      setEditId(null);
      setIsFormModalOpen(false);

      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });

      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
      } catch (err) {
        console.log("Full error:", err);

        const { errors: serverErrors, status, message } = extractApiError(err);

        if (serverErrors) {
          // Aadhaar duplicate / invalid
          if (serverErrors.aadhaar_number?.[0]) {
            setAadhaarError(serverErrors.aadhaar_number[0]);
          }

          // Email duplicate
          if (serverErrors.email?.[0]) {
            setEmailError(serverErrors.email[0]);
          }

          if (serverErrors.mobile?.[0]) {
              setMobileError(`❌ ${serverErrors.mobile[0]}`);
          }
        }

        const errMsg = status ? `[${status}] ${message}` : message;

        errorAlert("Save Failed", errMsg);
      }
  };

  const handleCloseModal = () => {
    setIsFormModalOpen(false);
    setEditing(false);
    setEditId(null);
    setFormData(emptyForm);
    setFileData({});
    setPincodeOptions([]);
    setSelectedPostOffice("");
    setEmailError("");
    setAadhaarError("");
    setMobileError("");
  };

  const handleEdit = (player) => {
    let parsedDisabilities = [];
    if (Array.isArray(player.disability_types)) {
      parsedDisabilities = player.disability_types;
    } else if (typeof player.disability_types === "string" && player.disability_types.trim()) {
      try {
        parsedDisabilities = JSON.parse(player.disability_types);
      } catch (_e) {
        parsedDisabilities = player.disability_types.split(",").map((s) => s.trim()).filter(Boolean);
      }
    }

    setFormData({
      ...emptyForm,
      name: player.name || "",
      relation: player.relation || "",
      dob: player.dob ? new Date(player.dob).toISOString().split("T")[0] : "",
      gender: player.gender || "",
      religion: player.religion || "",
      caste: player.caste || "",
      subcaste: player.subcaste || "",
      disability_types: parsedDisabilities,
      aadhaar_number: player.aadhaar_number || "",
      mobile: player.mobile || "",
      email: player.email || "",
      district: player.district || "",
      mandal: player.mandal || "",
      postoffice: player.postoffice || "",
      address: player.address || "",
      state: player.state || "",
      pincode: player.pincode || "",
      club: player.club || "",
      coach: player.coach || "",
    });

    // Store existing file paths separately
    setExistingFiles({
      photo: player.photo || null,
      aadhaar: player.aadhaar || null,
      birth_certificate: player.birth_certificate || null,
      sadarem: player.sadarem || null,
      others: player.others || null,
    });

    setFileData({}); // reset new uploads
    setSelectedPostOffice(player.postoffice || "");
    setEditId(player.id);
    setEditing(true);
    setIsFormModalOpen(true);
  };

  // eslint-disable-next-line no-unused-vars
  const handleDelete = async (id) => {
    const confirmed = await confirmAlert({
      title: "Delete Player?",
      text: "This will permanently delete the player",
      confirmText: "Delete",
    });

    if (!confirmed?.isConfirmed) return;

    try {
      await API.delete(`/admin/players/${id}`);
      setPlayers(players.filter((p) => p.id !== id));
      successAlert("Deleted", "Player deleted successfully");
    } catch {
      errorAlert("Error", "Failed to delete player");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleResetPlayerPassword = async (player) => {
    const result = await Swal.fire({
      title: `Reset password for ${player.name}?`,
      input: "password",
      inputLabel: "Enter new password",
      inputPlaceholder: "New password (min 6 chars)",
      inputAttributes: { autocapitalize: "off", autocorrect: "off" },
      showCancelButton: true,
      confirmButtonText: "Reset",
      inputValidator: (value) => {
        if (!value || value.length < 6) return "Password must be at least 6 characters";
        return null;
      },
    });

    if (!result.isConfirmed) return;

    try {
      await API.resetPlayerPassword(player.id, result.value);
      successAlert("Password reset", "Player password updated successfully");
    } catch (err) {
      errorAlert("Reset failed", err?.message || "Failed to reset player password");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleApprovalStatus = async (player, approvalStatus) => {
    const confirmed = await confirmAlert({
      title: `${approvalStatus} player?`,
      text: `${player.name} will be marked as ${approvalStatus}.`,
      confirmText: approvalStatus,
    });
    if (!confirmed?.isConfirmed) return;

    try {
      await API.put(`/admin/players/${player.id}/approval-status`, {
        approval_status: approvalStatus,
      });

      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });
      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
      successAlert("Updated", `Player marked as ${approvalStatus}`);
    } catch (err) {
      errorAlert("Update failed", err?.message || "Failed to update approval status");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleBlockStatus = async (player, shouldBlock) => {
    let blockedReason = "";

    if (shouldBlock) {
      const prompt = await Swal.fire({
        title: `Block ${player.name}?`,
        input: "text",
        inputLabel: "Reason (optional)",
        inputPlaceholder: "Reason for blocking login",
        showCancelButton: true,
        confirmButtonText: "Block",
      });

      if (!prompt.isConfirmed) return;
      blockedReason = String(prompt.value || "").trim();
    } else {
      const confirmed = await confirmAlert({
        title: `Release ${player.name}?`,
        text: "Player will be allowed to login again.",
        confirmText: "Release",
      });
      if (!confirmed?.isConfirmed) return;
    }

    try {
      await API.updatePlayerBlockStatus(player.id, shouldBlock, blockedReason);

      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });
      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
      successAlert("Updated", shouldBlock ? "Player blocked successfully" : "Player released successfully");
    } catch (err) {
      errorAlert("Update failed", err?.message || "Failed to update block status");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleFeeExemptionStatus = async (player, shouldExempt) => {
    const confirmed = await confirmAlert({
      title: shouldExempt ? `Grant ${feeExemptionLabel} fee exemption?` : `Remove ${feeExemptionLabel} fee exemption?`,
      text: shouldExempt
        ? `${player.name} will be exempted from ${feeExemptionLabel.toLowerCase()} event registration and escort fees.`
        : `${player.name} will be charged regular fees for ${feeExemptionLabel.toLowerCase()} events.`,
      confirmText: shouldExempt ? "Grant" : "Remove",
    });
    if (!confirmed?.isConfirmed) return;

    try {
      await API.updatePlayerFeeExemption(player.id, shouldExempt);

      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });
      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
      successAlert("Updated", shouldExempt ? `${feeExemptionLabel} fee exemption granted` : `${feeExemptionLabel} fee exemption removed`);
    } catch (err) {
      errorAlert("Update failed", err?.message || "Failed to update fee exemption");
    }
  };

  const handleFileDelete = async (id, field) => {
    const confirmed = await confirmAlert({
      title: `Delete ${field}?`,
      text: "This file will be removed permanently",
      confirmText: "Delete",
    });

    if (!confirmed?.isConfirmed) return;

    try {
      await API.delete(`/admin/players/${id}/file/${field}`);

      // Remove file from UI state immediately
      setExistingFiles((prev) => ({
        ...prev,
        [field]: null,
      }));

      setViewPlayer((prev) =>
        prev ? { ...prev, [field]: null } : prev
      );

      successAlert("Deleted", `${field} deleted successfully`);
    } catch (err) {
      errorAlert("Error", "Failed to delete file");
    }
  };

  // === SEARCH & PAGINATION ===
  // const filteredPlayers = useMemo(() => {
  //   const term = searchTerm.toLowerCase().trim();
  //   const skipFields = ["photo", "aadhaar", "birth_certificate", "sadarem", "others", "password"];

  //   return (players || []).filter((p) =>
  //     Object.keys(p).some((key) => {
  //       if (skipFields.includes(key)) return false;
  //       const value = p[key];
  //       return value && value.toString().toLowerCase().includes(term);
  //     })
  //   );
  // }, [players, searchTerm]);

  // === BULK ACTION HANDLERS ===
  const isAllSelected = useMemo(() => {
    if (players.length === 0) return false;
    return players.every((p) => selectedIds.includes(p.id));
  }, [players, selectedIds]);

  const toggleSelectAll = () => {
    if (isAllSelected) {
      const pagePlayerIds = players.map((p) => p.id);
      setSelectedIds((prev) => prev.filter((id) => !pagePlayerIds.includes(id)));
    } else {
      const pagePlayerIds = players.map((p) => p.id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pagePlayerIds])));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkApprove = async () => {
    if (!selectedIds.length) {
      warningAlert("No Selection", "Please select at least one player.");
      return;
    }
    const confirmed = await confirmAlert({
      title: "Approve Players?",
      text: `Approve ${selectedIds.length} selected player(s)?`,
      confirmText: "Approve All",
    });
    if (!confirmed?.isConfirmed) return;

    Swal.fire({
      title: "Updating...",
      text: `Approving ${selectedIds.length} player(s)...`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      let completed = 0;
      await batchRequests(selectedIds, async (id) => {
        const res = await API.put(`/admin/players/${id}/approval-status`, {
          approval_status: "Approved",
        });
        completed++;
        Swal.update({
          text: `Approved ${completed} of ${selectedIds.length} players...`,
        });
        return res;
      });
      Swal.close();
      successAlert("Approved", "Selected players approved successfully");
      setSelectedIds([]);
      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });
      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
    } catch (err) {
      Swal.close();
      errorAlert("Bulk update failed", err?.message || "Failed to approve players");
    }
  };

  const handleBulkReject = async () => {
    if (!selectedIds.length) {
      warningAlert("No Selection", "Please select at least one player.");
      return;
    }
    const targetIds = selectedIds.filter((id) => {
      const p = players.find((pl) => pl.id === id);
      return p && p.approval_status !== "Approved";
    });

    if (!targetIds.length) {
      warningAlert("Action Blocked", "Approved players cannot be rejected.");
      return;
    }

    const confirmed = await confirmAlert({
      title: "Reject Players?",
      text: `Reject ${targetIds.length} selected player(s)? (Approved players will be skipped)`,
      confirmText: "Reject",
    });
    if (!confirmed?.isConfirmed) return;

    Swal.fire({
      title: "Updating...",
      text: `Rejecting ${targetIds.length} player(s)...`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      let completed = 0;
      await batchRequests(targetIds, async (id) => {
        const res = await API.put(`/admin/players/${id}/approval-status`, {
          approval_status: "Rejected",
        });
        completed++;
        Swal.update({
          text: `Rejected ${completed} of ${targetIds.length} players...`,
        });
        return res;
      });
      Swal.close();
      successAlert("Rejected", "Selected non-approved players rejected successfully");
      setSelectedIds([]);
      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });
      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
    } catch (err) {
      Swal.close();
      errorAlert("Bulk update failed", err?.message || "Failed to reject players");
    }
  };

  const handleBulkToggleBlock = async () => {
    if (!selectedIds.length) {
      warningAlert("No Selection", "Please select at least one player.");
      return;
    }
    const confirmed = await confirmAlert({
      title: "Toggle Login Status?",
      text: `Toggle block/release logins for ${selectedIds.length} player(s) based on their current status?`,
      confirmText: "Toggle All",
    });
    if (!confirmed?.isConfirmed) return;

    Swal.fire({
      title: "Updating...",
      text: `Processing ${selectedIds.length} player(s)...`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      let completed = 0;
      await batchRequests(selectedIds, async (id) => {
        const player = players.find((p) => p.id === id);
        const currentlyBlocked = toBool(player?.is_blocked);
        const nextBlock = !currentlyBlocked;
        const blockedReason = nextBlock ? "Bulk blocked by administrator" : "";
        const res = await API.updatePlayerBlockStatus(id, nextBlock, blockedReason);
        completed++;
        Swal.update({
          text: `Updated ${completed} of ${selectedIds.length} players...`
        });
        return res;
      });
      Swal.close();
      successAlert("Updated", "Selected players' login block status toggled successfully");
      setSelectedIds([]);
      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });
      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
    } catch (err) {
      Swal.close();
      errorAlert("Bulk update failed", err?.message || "Failed to update block status");
    }
  };

  const handleBulkToggleFeeExemption = async () => {
    if (!selectedIds.length) {
      warningAlert("No Selection", "Please select at least one player.");
      return;
    }
    const confirmed = await confirmAlert({
      title: `Toggle ${feeExemptionLabel} Fee Exemption?`,
      text: `Toggle ${feeExemptionLabel.toLowerCase()} event fee exemption for ${selectedIds.length} player(s) based on their current status?`,
      confirmText: "Toggle All",
    });
    if (!confirmed?.isConfirmed) return;

    Swal.fire({
      title: "Updating...",
      text: `Processing ${selectedIds.length} player(s)...`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      let completed = 0;
      await batchRequests(selectedIds, async (id) => {
        const player = players.find((p) => p.id === id);
        const currentlyExempt = toBool(player?.[feeExemptionField]);
        const nextExempt = !currentlyExempt;
        const res = await API.updatePlayerFeeExemption(id, nextExempt);
        completed++;
        Swal.update({
          text: `Updated ${completed} of ${selectedIds.length} players...`
        });
        return res;
      });
      Swal.close();
      successAlert("Updated", `Selected players' ${feeExemptionLabel.toLowerCase()} fee exemption status updated successfully`);
      setSelectedIds([]);
      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });
      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
    } catch (err) {
      Swal.close();
      errorAlert("Bulk update failed", err?.message || "Failed to update fee exemption");
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) {
      warningAlert("No Selection", "Please select at least one player.");
      return;
    }
    const confirmed = await confirmAlert({
      title: `Delete ${selectedIds.length} player(s)?`,
      text: "This will permanently delete the selected players and cannot be undone.",
      confirmText: "Delete All",
    });
    if (!confirmed?.isConfirmed) return;

    Swal.fire({
      title: "Deleting...",
      text: `Deleting ${selectedIds.length} player(s)...`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      let completed = 0;
      await batchRequests(selectedIds, async (id) => {
        const res = await API.delete(`/admin/players/${id}`);
        completed++;
        Swal.update({
          text: `Deleted ${completed} of ${selectedIds.length} players...`
        });
        return res;
      });
      Swal.close();
      successAlert("Deleted", "Selected players deleted successfully");
      setSelectedIds([]);
      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });
      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
    } catch (err) {
      Swal.close();
      errorAlert("Bulk delete failed", err?.message || "Failed to delete players");
    }
  };

  const handleBulkResetPassword = async () => {
    if (!selectedIds.length) {
      warningAlert("No Selection", "Please select at least one player.");
      return;
    }

    const result = await Swal.fire({
      title: `Reset password for ${selectedIds.length} player(s)?`,
      input: "password",
      inputLabel: "Enter new password for selected players",
      inputPlaceholder: "New password (min 6 chars)",
      inputAttributes: { autocapitalize: "off", autocorrect: "off" },
      showCancelButton: true,
      confirmButtonText: "Reset All",
      inputValidator: (value) => {
        if (!value || value.length < 6) return "Password must be at least 6 characters";
        return null;
      },
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Resetting...",
      text: `Resetting password for ${selectedIds.length} player(s)...`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      let completed = 0;
      await batchRequests(selectedIds, async (id) => {
        const res = await API.resetPlayerPassword(id, result.value);
        completed++;
        Swal.update({
          text: `Reset ${completed} of ${selectedIds.length} players...`
        });
        return res;
      });
      Swal.close();
      successAlert("Password reset", "Selected players' passwords updated successfully");
      setSelectedIds([]);
      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });
      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
    } catch (err) {
      Swal.close();
      errorAlert("Reset failed", err?.message || "Failed to reset player passwords");
    }
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const sortedPlayers = useMemo(() => {
    if (!sortConfig.key) return players;
    return [...players].sort((a, b) => {
      let aVal = a[sortConfig.key] ?? "";
      let bVal = b[sortConfig.key] ?? "";
      if (Array.isArray(aVal)) aVal = aVal.join(", ");
      if (Array.isArray(bVal)) bVal = bVal.join(", ");
      const comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [players, sortConfig]);

  // === TABLE CONFIG ===
  const columns = [
    {
      header: (
        <input
          type="checkbox"
          checked={isAllSelected}
          onChange={toggleSelectAll}
          className="cursor-pointer rounded"
        />
      ),
      render: (p) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(p.id)}
          onChange={() => toggleSelectRow(p.id)}
          className="cursor-pointer rounded"
        />
      ),
    },
    {
      header: "S.No.",
      render: (_p, idx) => (page - 1) * limit + idx + 1,
    },
    {
      header: "Photo",
      render: (p) =>
        p.photo ? (
          <img
            src={resolveAssetUrl(p.photo)}
            alt={p.name || "Player Photo"}
            onClick={() => {
              setMediaModalSrc(resolveAssetUrl(p.photo));
              setMediaModalTitle(`${p.name} - Photo`);
            }}
            title="Click to view full screen"
            className="w-10 h-10 rounded-full object-cover mx-auto border cursor-pointer hover:opacity-80 transition"
          />
        ) : (
          "-"
        ),
    },
    { header: "Name", accessor: "name", sortKey: "name" },
    { header: "Gender", accessor: "gender", sortKey: "gender" },
    {
      header: "Disability Type",
      sortKey: "disability_types",
      render: (p) => {
        let list = [];
        if (Array.isArray(p.disability_types)) {
          list = p.disability_types;
        } else if (typeof p.disability_types === "string" && p.disability_types.trim()) {
          try {
            list = JSON.parse(p.disability_types);
          } catch (_e) {
            list = p.disability_types.split(",").map((s) => s.trim()).filter(Boolean);
          }
        }
        if (!list.length) return <span className="text-gray-400">-</span>;
        return (
          <div className="flex flex-wrap gap-1 justify-center max-w-[200px] mx-auto">
            {list.map((d, i) => (
              <span
                key={i}
                className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-800 border border-blue-200"
              >
                {d}
              </span>
            ))}
          </div>
        );
      },
    },
    { header: "Registration ID", accessor: "registration_id", sortKey: "registration_id" },
    { header: "Aadhaar", accessor: "aadhaar_number", sortKey: "aadhaar_number" },
    { header: "District", accessor: "district", sortKey: "district" },
    {
      header: "Approval",
      sortKey: "approval_status",
      render: (p) => {
        const status = String(p.approval_status || "Approved");
        const color =
          status === "Approved"
            ? "bg-green-100 text-green-700"
            : status === "Rejected"
              ? "bg-red-100 text-red-700"
              : "bg-yellow-100 text-yellow-700";
        return (
          <div className="flex flex-col items-center gap-1">
            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${color}`}>
              {status}
            </span>
            {p.district && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                {p.district}
              </span>
            )}
          </div>
        );
      },
    },
    {
      header: "Login",
      render: (p) => {
        const blocked = toBool(p.is_blocked);
        return (
          <span
            className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
              blocked ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
            }`}
          >
            {blocked ? "Blocked" : "Active"}
          </span>
        );
      },
    },
    {
      header: "Fee Exemption",
      render: (p) => {
        const districtExempt = toBool(p.is_district_fee_exempt);
        const stateExempt = toBool(p.is_state_fee_exempt);
        return (
          <div className="flex flex-wrap justify-center gap-1">
            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${districtExempt ? "bg-cyan-100 text-cyan-800" : "bg-slate-100 text-slate-600"}`}>District {districtExempt ? "Exempt" : "Regular"}</span>
            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${stateExempt ? "bg-violet-100 text-violet-800" : "bg-slate-100 text-slate-600"}`}>State {stateExempt ? "Exempt" : "Regular"}</span>
          </div>
        );
      },
    },
    {
      header: "Updated Time",
      accessor: "updated_at",
      sortKey: "updated_at",
      render: (p) => {
        if (!p.updated_at) return <span className="text-gray-400">-</span>;
        try {
          const d = new Date(p.updated_at);
          if (isNaN(d.getTime())) return <span className="text-[11px] text-slate-600">{String(p.updated_at)}</span>;
          const dateStr = d.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
          const timeStr = d.toLocaleTimeString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
          return (
            <div className="flex flex-col items-center justify-center whitespace-nowrap text-[11px] leading-tight text-slate-700">
              <span className="font-semibold text-slate-900">{dateStr}</span>
              <span className="text-[10px] text-slate-500 font-medium">{timeStr}</span>
            </div>
          );
        } catch (_e) {
          return <span className="text-[11px] text-slate-600">{String(p.updated_at)}</span>;
        }
      },
    },
    {
      header: "Actions",
      render: (p) => {
        return (
        <div className="flex flex-nowrap gap-1.5 max-w-[560px] overflow-x-auto py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button
            onClick={() => setViewPlayer(p)}
            className="bg-green-600 hover:bg-green-700 text-white text-[11px] px-2 py-1 rounded-md leading-none whitespace-nowrap"
          >
            View
          </button>
          <button
            onClick={() => handleEdit(p)}
            className="bg-yellow-500 hover:bg-yellow-600 text-white text-[11px] px-2 py-1 rounded-md leading-none whitespace-nowrap"
          >
            Edit
          </button>
          {isAdmin && (
            <button
              onClick={() => handleVisibilityChange(p)}
              className={`${toBool(p.is_hidden) ? "bg-cyan-700 hover:bg-cyan-800" : "bg-slate-700 hover:bg-slate-800"} text-white text-[11px] px-2 py-1 rounded-md leading-none whitespace-nowrap`}
            >
              {toBool(p.is_hidden) ? "Unhide" : "Hide"}
            </button>
          )}
          <button
            onClick={() => p.approval_status !== "Approved" && handleApprovalStatus(p, "Approved")}
            disabled={p.approval_status === "Approved"}
            className={`text-[11px] px-2 py-1 rounded-md leading-none whitespace-nowrap font-medium transition ${
              p.approval_status === "Approved"
                ? "bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
            }`}
            title={p.approval_status === "Approved" ? "Already Approved" : "Click to Approve"}
          >
            {p.approval_status === "Approved" ? "✓ Approved" : "Approve"}
          </button>
          <button
            onClick={() => p.approval_status !== "Approved" && p.approval_status !== "Rejected" && handleApprovalStatus(p, "Rejected")}
            disabled={p.approval_status === "Approved" || p.approval_status === "Rejected"}
            className={`text-[11px] px-2 py-1 rounded-md leading-none whitespace-nowrap font-medium transition ${
              p.approval_status === "Approved"
                ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed opacity-60"
                : p.approval_status === "Rejected"
                ? "bg-red-100 text-red-800 border border-red-300 font-bold cursor-not-allowed"
                : "bg-red-600 hover:bg-red-700 text-white shadow-sm"
            }`}
            title={
              p.approval_status === "Approved"
                ? "Approved player cannot be rejected"
                : p.approval_status === "Rejected"
                ? "Already Rejected"
                : "Click to Reject"
            }
          >
            {p.approval_status === "Rejected" ? "✕ Rejected" : "Reject"}
          </button>
        </div>
      );
      },
    },
  ];

  // === ROLE GUARD ===
  if (!role) return null;

  if (!["admin", "secretary", "district"].includes((role || "").toLowerCase())) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Unauthorized Access
      </div>
    );
  }

  // === UI ===
  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Player Registry</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Manage Players</h1>
              <p className="text-sm text-slate-600 mt-1">
                Maintain master player records, document verification, approvals and access controls.
              </p>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setEditId(null);
                  setFormData(emptyForm);
                  setFileData({});
                  setPincodeOptions([]);
                  setSelectedPostOffice("");
                  setEmailError("");
                  setAadhaarError("");
                  setMobileError("");
                  setIsFormModalOpen(true);
                }}
                className="mt-3 text-white font-semibold py-2 px-4 rounded-xl shadow-sm transition-all flex items-center gap-2 text-xs"
                style={{ backgroundColor: "#0e7490" }}
              >
                <span>➕</span> Create New Player Profile
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Records</p>
                <p className="text-lg font-semibold text-slate-900">{totalPlayers}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Page</p>
                <p className="text-lg font-semibold text-slate-900">{page}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Edit Mode</p>
                <p className="text-lg font-semibold text-emerald-800">{editing ? "On" : "Off"}</p>
              </div>
              <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-cyan-700">Total Pages</p>
                <p className="text-lg font-semibold text-cyan-800">{totalPages}</p>
              </div>
            </div>
          </div>
        </section>

        {/* === FORM === */}
        <Modal
          isOpen={isFormModalOpen}
          onClose={handleCloseModal}
          title={editing ? "Edit Player Profile" : "Create New Player Profile"}
          width="800px"
        >
          <form
            onSubmit={handleSubmit}
            className="mb-0"
          >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {[
            { name: "name", placeholder: "NAME", required: true },
            { name: "relation", placeholder: "RELATION" },
            { name: "dob", type: "date" },
            {
              name: "gender",
              type: "select",
              placeholder: "GENDER",
              options: ["Male", "Female", "Other"],
            },
            {
              name: "religion",
              type: "select",
              placeholder: "RELIGION",
              options: ["Hindu", "Muslim", "Christian", "Sikh", "Buddhist", "Jain", "Other"],
            },
            {
              name: "caste",
              type: "select",
              placeholder: "CASTE / CATEGORY",
              options: ["OC / General", "BC-A", "BC-B", "BC-C", "BC-D", "BC-E", "SC", "ST", "Other"],
            },
            { name: "subcaste", placeholder: "SUBCASTE" },
            // Aadhaar field with error
            { name: "aadhaar_number", placeholder: "AADHAAR NUMBER (12 digits)", required: true },
            { name: "mobile", placeholder: "MOBILE" },
            // Email field with error
            { name: "email", type: "email", placeholder: "EMAIL" },
            { name: "pincode", placeholder: "PINCODE" },
            { name: "postoffice_ui", type: "postoffice" },
            { name: "mandal", placeholder: "MANDAL" },
            { name: "district", placeholder: "DISTRICT" },
            { name: "address", placeholder: "ADDRESS" },
            { name: "state", placeholder: "STATE" },
            { name: "club", placeholder: "CLUB" },
            { name: "coach", placeholder: "ASSISTANT COACH" },
          ].map((f) => {
            if (f.type === "select") {
              return (
                <SearchableSelect
                  key={f.name}
                  name={f.name}
                  value={formData[f.name]}
                  onChange={handleChange}
                  required={f.required}
                  className="border p-2 rounded"
                >
                  <option value="">{`Select ${f.placeholder || f.name}`}</option>
                  {f.options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </SearchableSelect>
              );
            }

            // Aadhaar input with error display
            if (f.name === "aadhaar_number") {
              return (
                <div key="aadhaar_wrapper" className="space-y-1">
                  <input
                    type="text"
                    name="aadhaar_number"
                    value={formData.aadhaar_number}
                    onChange={handleChange}
                    onBlur={handleAadhaarBlur}
                    placeholder="AADHAAR NUMBER (12 digits)"
                    required
                    className={`border p-2 rounded w-full transition-all ${
                      aadhaarError 
                        ? "border-red-500 bg-red-50 ring-2 ring-red-200" 
                        : "border-gray-300 hover:border-blue-300"
                    }`}
                    maxLength="12"
                  />
                  {aadhaarError && (
                    <div className="text-red-600 text-xs bg-red-50 border border-red-200 p-2 rounded-md shadow-sm">
                      {aadhaarError}
                    </div>
                  )}
                </div>
              );
            }

            if (f.name === "mobile") {
                return (
                    <div key="mobile_wrapper" className="space-y-1">
                        <input
                            type="text"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleChange}
                            onBlur={handleMobileBlur}
                            placeholder="MOBILE"
                            className={`border p-2 rounded w-full transition-all ${
                                mobileError
                                    ? "border-red-500 bg-red-50 ring-2 ring-red-200"
                                    : "border-gray-300 hover:border-blue-300"
                            }`}
                            maxLength="15"
                        />

                        {mobileError && (
                            <div className="text-red-600 text-xs bg-red-50 border border-red-200 p-2 rounded-md shadow-sm">
                                {mobileError}
                            </div>
                        )}
                    </div>
                );
            }

            // Email input with error display
            if (f.name === "email") {
              return (
                <div key="email_wrapper" className="space-y-1">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleEmailBlur}
                    placeholder="EMAIL"
                    className={`border p-2 rounded w-full transition-all ${
                      emailError 
                        ? "border-red-500 bg-red-50 ring-2 ring-red-200" 
                        : "border-gray-300 hover:border-blue-300"
                    }`}
                  />
                  {emailError && (
                    <div className="text-red-600 text-xs bg-red-50 border border-red-200 p-2 rounded-md shadow-sm">
                      {emailError}
                    </div>
                  )}
                </div>
              );
            }

            // Other fields unchanged
            if (f.type === "postoffice") {
              return (
                <SearchableSelect
                  ref={villageRef}
                  key="postoffice_ui"
                  value={selectedPostOffice}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedPostOffice(val);
                    const selected = pincodeOptions.find((x) => String(x.postoffice) === String(val));
                    setFormData((prev) => ({ ...prev, postoffice: val }));
                    if (!selected) return;
                    setFormData((prev) => ({
                      ...prev,
                      postoffice: val,
                      mandal: selected.mandal || "",
                      state: selected.state || prev.state || "",
                    }));
                  }}
                  disabled={pincodeOptions.length === 0}
                  className="border p-2 rounded"
                >
                  <option value="">
                    {pincodeOptions.length ? "Select Village / Post Office" : "Enter 6-digit pincode first"}
                  </option>
                  {pincodeOptions.map((opt, idx) => (
                    <option key={`${opt.postoffice}-${idx}`} value={opt.postoffice}>
                      {opt.postoffice}
                    </option>
                  ))}
                </SearchableSelect>
              );
            }

            if (f.name === "district") {
              return (
                <SearchableSelect
                  key="district_select_ui"
                  name="district"
                  value={formData.district || ""}
                  onChange={handleChange}
                  required
                  className="border p-2 rounded"
                >
                  <option value="">Select District *</option>
                  {combinedDistrictOptions.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </SearchableSelect>
              );
            }

            if (f.name === "mandal") {
              return (
                <input
                  key="mandal"
                  type="text"
                  name="mandal"
                  value={formData.mandal}
                  onChange={handleChange}
                  placeholder={f.placeholder}
                  required={f.required}
                  list="admin-mandal-options"
                  className="border p-2 rounded"
                />
              );
            }

            // Other fields unchanged
            return (
              <input
                key={f.name}
                type={f.type || "text"}
                name={f.name}
                value={formData[f.name]}
                onChange={handleChange}
                placeholder={f.placeholder}
                required={f.required}
                className="border p-2 rounded"
              />
            );
          })}
          <datalist id="admin-mandal-options">
            {mandalOptions.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
          <datalist id="admin-district-options">
            {combinedDistrictOptions.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
        </div>

        <div className="mt-4">
          <DisabilitySelect
            selected={formData.disability_types}
            onChange={(selected) => setFormData((prev) => ({ ...prev, disability_types: selected }))}
            required
          />
        </div>

          {/* File Uploads */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4">
            {["photo", "aadhaar", "birth_certificate", "sadarem", "others"].map(
              (field) => (
                <div key={field} className="border rounded p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.replace("_", " ").toUpperCase()}
                  </label>
                  {(fileData[field] || (editing && existingFiles[field])) && (
                    <div className="flex flex-col items-center mb-2">
                      {(() => {
                        const newFile = fileData[field];
                        const existingPath = existingFiles[field];
                        const targetSrc = newFile ? URL.createObjectURL(newFile) : resolveAssetUrl(existingPath);
                        const isImg = newFile ? newFile.type.startsWith("image/") : /\.(jpg|jpeg|png)$/i.test(existingPath || "");
                        const labelText = newFile ? `View Selected ${field.replace("_", " ")}` : `View Current ${field.replace("_", " ")}`;

                        return (
                          <>
                            {isImg ? (
                              <img
                                src={targetSrc}
                                alt={field}
                                className="w-20 h-20 object-cover rounded border mb-1 cursor-pointer hover:opacity-80 transition"
                                onClick={() => openMediaModal(targetSrc, `${field.replace("_", " ")} ${newFile ? "(New)" : "(Current)"}`)}
                              />
                            ) : (
                              <button
                                type="button"
                                onClick={() => openMediaModal(targetSrc, `${field.replace("_", " ")} ${newFile ? "(New PDF)" : "(Current PDF)"}`)}
                                className="text-blue-600 text-xs hover:underline font-semibold my-1"
                              >
                                {labelText}
                              </button>
                            )}
                          </>
                        );
                      })()}
                      {editing && existingFiles[field] && !fileData[field] && (
                        <button
                          type="button"
                          onClick={() => handleFileDelete(editId, field)}
                          className="text-red-600 text-xs hover:underline mt-1"
                        >
                          Delete Current File
                        </button>
                      )}
                    </div>
                  )}
                  <input
                    type="file"
                    name={field}
                    onChange={handleFileChange}
                    accept="image/*,.pdf"
                    required={!editing && ["photo", "aadhaar", "birth_certificate", "sadarem"].includes(field)}
                    className="border p-1 rounded w-full text-sm"
                  />
                </div>
              )
            )}
          </div>

          <div className="text-[11px] text-amber-700 bg-amber-50 p-2.5 rounded-lg border border-amber-200 text-left w-full mt-3">
            💡 <strong>Checklist:</strong> Validate Aadhaar/email before saving. Upload clear identity documents for approval.
          </div>

          <div className="flex justify-end gap-3 mt-5 pt-4 border-t w-full">
            <button
              type="button"
              onClick={async () => {
                const confirmed = await confirmAlert({
                  title: "Cancel?",
                  text: "Unsaved changes will be lost",
                  confirmText: "Yes, Cancel",
                });
                if (confirmed?.isConfirmed) {
                  handleCloseModal();
                }
              }}
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 font-semibold text-sm transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 font-semibold transition-all shadow-sm text-sm"
            >
              {editing ? "Update Player" : "Add Player"}
            </button>
          </div>
        </form>
      </Modal>

        {/* === Search & Table === */}
        <section className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm">
          {(isAdmin || canManageFeeExemption) && (
            <div className="mb-3 inline-flex rounded-xl border border-slate-300 bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => { setVisibilityFilter("visible"); setPage(1); }}
                className={`rounded-lg px-4 py-2 text-xs font-bold ${visibilityFilter === "visible" ? "bg-white text-slate-900 shadow" : "text-slate-600"}`}
              >
                Visible Players
              </button>
              {isAdmin && <button
                type="button"
                onClick={() => { setVisibilityFilter("hidden"); setPage(1); }}
                className={`rounded-lg px-4 py-2 text-xs font-bold ${visibilityFilter === "hidden" ? "bg-slate-800 text-white shadow" : "text-slate-600"}`}
              >
                Hidden Players
              </button>}
              {canManageFeeExemption && <button
                type="button"
                onClick={() => { setVisibilityFilter("exempted"); setPage(1); }}
                className={`rounded-lg px-4 py-2 text-xs font-bold ${visibilityFilter === "exempted" ? "bg-emerald-700 text-white shadow" : "text-slate-600"}`}
              >
                Fee Exempted
              </button>}
            </div>
          )}
          <SearchBar
            placeholder="Search by Any Column..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {visibilityFilter === "visible" && <ExportButtons table="players" searchTerm={searchTerm} />}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-4 mt-4 flex flex-wrap items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center justify-between gap-3 mb-1 w-full">
              <span className="text-sm font-semibold text-slate-800">
                Bulk Actions {selectedIds.length > 0 ? `(${selectedIds.length} selected)` : "(Select checkboxes in the table to use)"}
              </span>
              {selectedIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedIds([])}
                  className="text-xs text-red-600 hover:underline font-semibold"
                >
                  Clear Selection
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleBulkApprove}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-xs font-semibold shadow-sm"
              >
                Approve Selected
              </button>
              <button
                type="button"
                onClick={handleBulkReject}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-xs font-semibold shadow-sm"
              >
                Reject Selected
              </button>
              {canManageFeeExemption && (
                <>
                  {canManageAccounts && <>
                  <button
                    type="button"
                    onClick={handleBulkToggleBlock}
                    className="bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded text-xs font-semibold"
                  >
                    Block/Release Logins
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkResetPassword}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-semibold"
                  >
                    Reset Passwords
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkDelete}
                    className="bg-red-800 hover:bg-red-900 text-white px-3 py-1.5 rounded text-xs font-semibold"
                  >
                    Delete Selected
                  </button>
                  </>}
                  <button
                    type="button"
                    onClick={handleBulkToggleFeeExemption}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded text-xs font-semibold"
                  >
                    Grant/Remove {feeExemptionLabel} Exemption
                  </button>
                </>
              )}
            </div>
          </div>
          <DataTable
            columns={columns}
            data={sortedPlayers}
            page={page}
            totalPages={totalPages}
            setPage={setPage}
            limit={limit}
            setLimit={handleSetLimit}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </section>
      </div>

      {/* === View Player Modal === */}
      <Modal
        isOpen={!!viewPlayer}
        onClose={() => setViewPlayer(null)}
        title={viewPlayer?.name}
        width="600px"
      >
        {viewPlayer && (
          <>
            <div className="space-y-2 text-sm">
              {[
                "relation",
                "gender",
                "religion",
                "caste",
                "subcaste",
                "dob",
                "registration_id",
                "aadhaar_number",
                "mobile",
                "email",
                "district",
                "mandal",
                "state",
                "pincode",
                "address",
                "club",
                "coach",
              ].map((key) => (
                <p key={key}>
                  <strong className="capitalize">{key.replace("_", " ")}:</strong>{" "}
                  {viewPlayer[key] || "-"}
                </p>
              ))}
              <p>
                <strong>Disability Type(s):</strong>{" "}
                {Array.isArray(viewPlayer.disability_types)
                  ? viewPlayer.disability_types.join(", ")
                  : typeof viewPlayer.disability_types === "string"
                  ? viewPlayer.disability_types
                  : "-"}
              </p>
              <p>
                <strong>Approval Status:</strong> {viewPlayer.approval_status || "Approved"}
              </p>
              <p>
                <strong>Login Status:</strong> {toBool(viewPlayer.is_blocked) ? "Blocked" : "Active"}
              </p>
              <p>
                <strong>Block Reason:</strong> {viewPlayer.blocked_reason || "-"}
              </p>
              <p>
                <strong>Event Fee Exemption:</strong> {toBool(viewPlayer.is_fee_exempt) ? "Enabled" : "Disabled"}
              </p>
              <p>
                <strong>Last Updated:</strong>{" "}
                {viewPlayer.updated_at ? new Date(viewPlayer.updated_at).toLocaleString("en-IN") : "-"}
              </p>
            </div>

            <div className="mt-4">
              <h3 className="font-semibold mb-2">Documents (Click to view full screen)</h3>
              <div className="grid grid-cols-2 gap-3 text-center">
                {["photo", "aadhaar", "birth_certificate", "sadarem", "others"].map(
                  (file) => {
                    const filePath = viewPlayer[file];
                    if (!filePath) {
                      return (
                        <div key={file} className="p-3 border rounded bg-slate-50">
                          <span className="text-gray-400 text-xs capitalize">No {file.replace("_", " ")}</span>
                        </div>
                      );
                    }

                    const url = resolveAssetUrl(filePath);
                    const isImg = /\.(jpg|jpeg|png)$/i.test(filePath);

                    return (
                      <div key={file} className="p-2 border rounded-lg bg-slate-50 flex flex-col items-center gap-1.5">
                        <span className="text-xs font-semibold text-slate-700 capitalize">{file.replace("_", " ")}</span>
                        {isImg ? (
                          <img
                            src={url}
                            alt={file}
                            onClick={() => {
                              setMediaModalSrc(url);
                              setMediaModalTitle(`${viewPlayer.name} - ${file.replace("_", " ")}`);
                            }}
                            title="Click to view full screen"
                            className="w-20 h-20 object-cover rounded border cursor-pointer hover:opacity-80 transition shadow-sm"
                          />
                        ) : (
                          <button
                            onClick={() => {
                              setMediaModalSrc(url);
                              setMediaModalTitle(`${viewPlayer.name} - ${file.replace("_", " ")}`);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-md font-medium transition"
                          >
                            View Document
                          </button>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* === Media Viewer Modal === */}
      <MediaViewerModal
        isOpen={!!mediaModalSrc}
        onClose={() => setMediaModalSrc(null)}
        src={mediaModalSrc}
        title={mediaModalTitle}
      />
    </>
  );
};

export default ManagePlayers;

